import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMySSOUrl } from '@sentara-health/sentara-api-hooks-core';
import { errorhandler, domainForSegment, ANALYTICS_MESSAGES } from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import {
  Headings,
  Button,
  Paragraph,
  Loader,
  ErrorMessage,
  ExternalLinkIcon,
} from '@sentara-health/optimahealth_web';

export type InterimViewType = 'findadoc' | 'changepcp';

interface InterimViewProps {
  type: InterimViewType;
}

function InterimView({ type }: InterimViewProps) {
  const { t } = useTranslation();
  const { SSOUrlType } = useMySSOUrl();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const { contentListingItemClicked } = useAdobeAnalytics();
  const effectRan = useRef(false);
  const analyticsFeatureName =
    type === 'findadoc' ? 'Find_doctor' : 'Primary_care_physician_change';

  useEffect(() => {
    if (effectRan.current) return;
    contentListingItemClicked({
      segment: `${domainForSegment}${window.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: '1001',
              contentTitle: `${analyticsFeatureName}_${ANALYTICS_MESSAGES.SSO_INTERSTITIAL_PAGE_LOAD}`,
              itemPosition: 1,
            },
          ],
        },
      },
    });
    effectRan.current = true;
  }, [analyticsFeatureName, contentListingItemClicked]);

  useEffect(() => {
    setErrorMessage(false);
  }, [type]);

  // Function to get and open the API URL
  const getAPIUrl = async () => {
    setLoading(true);
    const data = 'HelathSparqOneSSOUrl';
    const response = await SSOUrlType(data);
    if (response?.data?.ssoUrl) {
      contentListingItemClicked({
        segment: `${domainForSegment}${window.location.pathname}`,
        contentListing: {
          eventType: 'Content Listing Item Clicked',
          listing: {
            content: [
              {
                contentId: '1004',
                contentTitle: `${analyticsFeatureName}_${ANALYTICS_MESSAGES.SSO_LAUNCH_SUCCESS}`,
                itemPosition: 4,
              },
            ],
          },
        },
      });
      window.open(response.data.ssoUrl, '_blank');
    } else if (response?.errorCode) {
      errorhandler(response?.errorCode);
      contentListingItemClicked({
        segment: `${domainForSegment}${window.location.pathname}`,
        contentListing: {
          eventType: 'Content Listing Item Clicked',
          listing: {
            content: [
              {
                contentId: '1006',
                contentTitle: `${analyticsFeatureName}_${ANALYTICS_MESSAGES.SSO_ERROR}`,
                itemPosition: 6,
              },
            ],
          },
        },
      });
      setErrorMessage(true);
    }
    setLoading(false);
  };

  // Get title and message based on type
  const getTitle = () => {
    return type === 'findadoc'
      ? t('common.findADoctor')
      : t('common.changePrimaryCare');
  };

  const getMessage = () => {
    return type === 'findadoc'
      ? t('interimview.findadocMsg')
      : t('interimview.changepcpMsg');
  };

  return (
    <div>
      {loading ? <Loader /> : null}
      <div className="mb-4">
        <Headings
          level={1}
          text={getTitle()}
          variant="semiBold"
          dataTestId="pharmacyHeader"
        />
      </div>

      {errorMessage && (
        <ErrorMessage>{t('common.internalServerError')}</ErrorMessage>
      )}

      <div className="pharmacy_claims_wrapper mb-4">
        <Paragraph
          text={getMessage()}
          variant="regular"
          data-testid="pharmacyClaimsText"
        />
        <div className="d-flex align-items-center">
          <Button
            dataTestId={'continueNavigate'}
            className="btn-links me-0"
            onClick={() => {
              getAPIUrl();
            }}
            variant="link_btn"
            iconRight={<ExternalLinkIcon />}
            iconStroke="none"
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default InterimView;
